import { useState, type ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';

import { ActionDialog } from './action-dialog';
import type { ActionDefinition, AnyActionDefinition } from './define-action';

interface Pending {
  readonly serial: number;
  readonly action: AnyActionDefinition;
  readonly ctx?: unknown;
  readonly batch?: readonly unknown[];
  readonly onDone?: ((results: readonly unknown[]) => void) | undefined;
}

export interface ActionDialogs {
  /** Abre o diálogo da ação para um registro. */
  open<C, V extends FieldValues, R>(action: ActionDefinition<C, V, R>, ctx: C, onDone?: (results: readonly R[]) => void): void;
  /** Abre o diálogo em lote: o `submit` roda por item (02 §11.1). */
  openBatch<C, V extends FieldValues, R>(action: ActionDefinition<C, V, R>, items: readonly C[], onDone?: (results: readonly R[]) => void): void;
  readonly isOpen: boolean;
  /** Ponha uma vez na tela: é onde o diálogo aberto aparece. */
  readonly element: ReactNode;
}

/**
 * Um lugar só por tela para os diálogos de ação: a barra, o menu "Mais ações", o lote e os blocos
 * pedem `open(ação, contexto)`. O último diálogo fica montado depois de fechar, para a saída animar
 * pelo mesmo caminho (U38).
 */
export function useActionDialogs(): ActionDialogs {
  const [pending, setPending] = useState<Pending | null>(null);
  const [isOpen, setOpen] = useState(false);

  const show = (next: Omit<Pending, 'serial'>) => {
    setPending((previous) => ({ ...next, serial: (previous?.serial ?? 0) + 1 }));
    setOpen(true);
  };

  return {
    open: (action, ctx, onDone) => show({ action, ctx, onDone: onDone as Pending['onDone'] }),
    openBatch: (action, items, onDone) => show({ action, batch: items, onDone: onDone as Pending['onDone'] }),
    isOpen,
    element: pending ? (
      <ActionDialog
        key={pending.serial}
        action={pending.action}
        {...(pending.batch ? { batch: pending.batch } : { ctx: pending.ctx })}
        open={isOpen}
        onOpenChange={setOpen}
        {...(pending.onDone ? { onDone: pending.onDone } : {})}
      />
    ) : null,
  };
}
